import { Controller } from "@hotwired/stimulus"

// Polls /signal/:tasker_id/pulse every INTERVAL_MS and updates live fields.
// Debounces: never starts a new fetch while one is in-flight. If a fetch
// takes longer than the interval, the next tick is skipped and we wait.
// Visible countdown ticks every second so the MC knows when the next
// refresh will land.
export default class extends Controller {
  static targets = [
    "countdown",
    "lastSeenLabel", "statusDot", "statusLabel",
    "shiftLabel",
    "offlineMode", "lastSignIn",
    "logList", "logEmpty"
  ]
  static values = {
    pulseUrl: String,
    intervalMs: { type: Number, default: 10000 },
    maxLogId: { type: Number, default: 0 }
  }

  connect() {
    this._inFlight = false
    this._secondsLeft = Math.floor(this.intervalMsValue / 1000)
    this._renderCountdown()
    this._tickInterval = setInterval(() => this._tick(), 1000)
    this._visHandler = () => this._onVisibilityChange()
    document.addEventListener("visibilitychange", this._visHandler)
  }

  disconnect() {
    clearInterval(this._tickInterval)
    if (this._abort) this._abort.abort()
    document.removeEventListener("visibilitychange", this._visHandler)
  }

  _onVisibilityChange() {
    // Pause polling when the tab is hidden; resume when visible
    if (document.hidden) {
      // No need to clear the interval — _tick is a no-op while hidden.
    } else {
      // Refresh immediately when coming back into view
      this._secondsLeft = 0
      this._tick()
    }
  }

  _tick() {
    if (document.hidden) return
    if (this._inFlight) {
      this._renderCountdown(true) // show "waiting" label while fetch outstanding
      return
    }
    this._secondsLeft -= 1
    if (this._secondsLeft <= 0) {
      this._fetch()
    }
    this._renderCountdown()
  }

  async _fetch() {
    this._inFlight = true
    this._abort = new AbortController()
    this._renderCountdown(true)
    try {
      const url = `${this.pulseUrlValue}?since_log_id=${this.maxLogIdValue}`
      const resp = await fetch(url, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
        signal: this._abort.signal
      })
      if (!resp.ok) throw new Error(`pulse ${resp.status}`)
      const data = await resp.json()
      this._apply(data)
    } catch (e) {
      if (e.name !== "AbortError") console.warn("[signal-poller]", e)
    } finally {
      this._inFlight = false
      this._secondsLeft = Math.floor(this.intervalMsValue / 1000)
      this._renderCountdown()
    }
  }

  _apply(data) {
    if (this.hasLastSeenLabelTarget && data.last_seen_label) {
      this.lastSeenLabelTarget.textContent = data.last_seen_label
    }
    if (this.hasStatusLabelTarget && data.status_label) {
      this.statusLabelTarget.textContent = data.status_label
      // NOTE: text colour class (text-emerald-600 / text-amber-600 / text-slate-400) is not updated
      // here because the pulse endpoint does not yet return status_text_class. The initial render
      // from show.html.erb is correct; a full-page tab switch will re-render with the right colour.
    }
    if (this.hasStatusDotTarget && data.status_dot_class) {
      // Replace any bg-* class with the new one
      this.statusDotTarget.classList.remove("bg-emerald-500", "bg-amber-400", "bg-slate-300")
      this.statusDotTarget.classList.add(data.status_dot_class)
    }
    if (this.hasShiftLabelTarget && data.shift_status_label) {
      this.shiftLabelTarget.textContent = data.shift_status_label
      if (data.shift_status_class) {
        this.shiftLabelTarget.className = data.shift_status_class
      }
    }
    if (this.hasOfflineModeTarget && data.field_offline_mode) {
      this.offlineModeTarget.textContent = data.field_offline_mode
    }
    if (this.hasLastSignInTarget && data.last_sign_in_at_label) {
      this.lastSignInTarget.textContent = data.last_sign_in_at_label
    }

    // New log entries — prepend in chronological-newest-first order
    if (data.new_logs && data.new_logs.length > 0 && this.hasLogListTarget) {
      // If there was an empty-state element, hide it
      if (this.hasLogEmptyTarget) this.logEmptyTarget.style.display = "none"
      data.new_logs.forEach(log => {
        const row = document.createElement("div")
        row.className = "px-4 py-2.5 text-xs flex items-start gap-3 bg-yellow-50/40 transition-colors"
        row.innerHTML = `
          <span class="${log.level_class} rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide shrink-0">${this._escape(log.level)}</span>
          <span class="text-slate-400 font-mono shrink-0">${this._escape(log.logged_at_label)}</span>
          <span class="text-slate-500 shrink-0">${this._escape(log.category || "")}</span>
          <span class="text-slate-700 flex-1 break-words">${this._escape(log.message || "")}</span>
        `
        this.logListTarget.prepend(row)
        // Fade the highlight off after a moment
        setTimeout(() => row.classList.remove("bg-yellow-50/40"), 1500)
      })
      this.maxLogIdValue = Math.max(this.maxLogIdValue, data.max_log_id || 0)
    }
  }

  _renderCountdown(isFetching = false) {
    if (!this.hasCountdownTarget) return
    if (isFetching) {
      this.countdownTarget.textContent = "…"
    } else {
      const s = Math.max(0, this._secondsLeft)
      this.countdownTarget.textContent = String(s)
    }
  }

  _escape(s) {
    const d = document.createElement("div")
    d.textContent = String(s)
    return d.innerHTML
  }
}
